import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, MapPin, Palmtree } from 'lucide-react';
import api from '../../services/api.js';
import { assetUrl } from '../../utils/assetUrl.js';
import { formatMoneyCOP } from '../../utils/formatMoney.js';
import BrandLoading from '../../components/ui/BrandLoading.jsx';
import PremiumCard from '../../components/ui/PremiumCard.jsx';
import ScrollReveal from '../../components/public/ScrollReveal.jsx';
import PublicPageHero from '../../components/public/PublicPageHero.jsx';
import VacacionesReglamentoPanel from '../../components/public/VacacionesReglamentoPanel.jsx';
import { vacacionesReglamento } from '../../content/vacacionesReglamento.js';
import publicCta from '../../content/publicCta.json';
import { getVacacionWhatsAppHref } from '../../utils/vacacionWhatsApp.js';
import { useVisibleLoading } from '../../hooks/useVisibleLoading.js';

function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString('es-CO', { day: 'numeric', month: 'long', year: 'numeric' });
}

export default function SedesVacacionalesPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const showLoading = useVisibleLoading(loading);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const { data: res } = await api.get('/v1/public/vacaciones');
        if (cancelled) return;
        const raw = res?.data;
        setItems(Array.isArray(raw) ? raw : []);
      } catch (e) {
        if (!cancelled) setError(e.message || 'No se pudieron cargar las sedes vacacionales');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="min-h-[60vh] pb-16 lg:pb-24">
      <PublicPageHero
        eyebrow="Beneficios"
        title="Sede vacacional Tocaima"
        description={`Estadía en ${vacacionesReglamento.ubicacion} para asociados, familia y visitantes. Las tarifas, horarios y reservas se rigen por el reglamento vigente (Acuerdo 31/07/2024).`}
      />

      <section className="border-b border-slate-200 bg-slate-50 py-14 lg:py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          {error && (
            <p className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</p>
          )}

          <div className={error ? 'mt-10' : ''}>
            {showLoading && (
              <BrandLoading variant="inline" tone="light" label="Cargando sedes…" className="min-h-[40vh]" />
            )}

            {!showLoading && !error && items.length === 0 && (
              <p className="text-center text-slate-600">No hay sedes vacacionales publicadas por ahora.</p>
            )}

            {!showLoading && items.length > 0 && (
              <div className="grid gap-8 lg:grid-cols-2">
                {items.map((x, i) => {
                  const waHref = getVacacionWhatsAppHref(x);
                  return (
                    <ScrollReveal key={x.id} delayMs={Math.min(i, 6) * 50} className="min-w-0">
                      <PremiumCard hover className="overflow-hidden !p-0">
                        <div className="grid min-w-0 gap-0 md:grid-cols-5 md:items-stretch">
                          <div className="relative flex min-h-0 min-w-0 md:col-span-2">
                            <div className="flex aspect-[4/3] w-full min-h-[12rem] items-center justify-center bg-white p-4 ring-1 ring-slate-200 md:aspect-auto md:h-full md:min-h-full">
                              {x.imagen ? (
                                <img
                                  src={assetUrl(x.imagen)}
                                  alt=""
                                  className="mx-auto block max-h-full max-w-full object-contain object-center transition duration-700 group-hover:scale-105"
                                />
                              ) : (
                                <div className="flex h-full min-h-[12rem] flex-col items-center justify-center gap-2 px-4 text-center text-slate-400">
                                  <Palmtree className="h-10 w-10 text-accent-lime" />
                                  <span className="text-sm">Sede vacacional</span>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="min-w-0 space-y-4 border-t border-white/5 bg-slate-950/40 p-8 md:col-span-3 md:border-l md:border-t-0">
                            <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500">
                              <span className="inline-flex items-center gap-1 rounded-full border border-[#1acb12]/35 bg-[#1acb12]/12 px-3 py-1 text-accent-lime">
                                <MapPin className="h-3.5 w-3.5" />
                                {x.ubicacion || 'Colombia'}
                              </span>
                              <span className="inline-flex items-center gap-1">
                                <Calendar className="h-3.5 w-3.5 text-accent-lime" />
                                Vigencia: {formatDate(x.fecha_inicio)} — {formatDate(x.fecha_fin)}
                              </span>
                            </div>
                            <h2 className="font-display text-2xl font-semibold text-white md:text-3xl">{x.titulo}</h2>
                            <p className="text-justify text-sm leading-relaxed text-slate-400">{x.descripcion}</p>
                            <div className="flex flex-wrap items-end justify-between gap-4 pt-2">
                              <div>
                                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                  Desde (por noche)
                                </p>
                                <p className="font-display text-2xl font-bold text-accent-lime">
                                  {Number(x.precio) > 0 ? formatMoneyCOP(x.precio) : 'Consultar'}
                                </p>
                                <p className="mt-1 text-xs text-slate-500">Según reglamento y día de la semana</p>
                              </div>
                              <div className="flex flex-wrap gap-2">
                                <Link
                                  to={`/sedes-vacacionales/${x.id}`}
                                  className="inline-flex rounded-full border border-[#1acb12]/40 bg-[#1acb12]/10 px-5 py-2 text-sm font-semibold text-accent-lime transition hover:bg-[#1acb12]/20"
                                >
                                  {publicCta.vacacionesDetailLinkLabel}
                                </Link>
                                {waHref ? (
                                  <a
                                    href={waHref}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex rounded-full bg-black px-5 py-2 text-sm font-semibold text-white transition hover:bg-neutral-900"
                                  >
                                    {publicCta.vacacionesCardLinkLabel}
                                  </a>
                                ) : null}
                              </div>
                            </div>
                          </div>
                        </div>
                      </PremiumCard>
                    </ScrollReveal>
                  );
                })}
              </div>
            )}
          </div>

          {!showLoading && items.length > 0 && (
            <p className="mt-14 text-center text-sm text-slate-600">
              Reserva con el formato oficial y envío a la cooperativa. Cupos sujetos a disponibilidad.{' '}
              <Link to="/contacto" className="font-semibold text-accent-lime hover:text-[#0d2b7f]">
                Contáctanos
              </Link>
              .
            </p>
          )}

          <div id="reglamento" className="mt-14 lg:mt-20">
            <VacacionesReglamentoPanel />
          </div>
        </div>
      </section>
    </div>
  );
}
